import { getBackendUrl } from "@Utils";
import type { ProjectData } from "@Reducers/breadcrumbsdata";
import type { useFormValidator } from "@Features/forms";
import { useTranslation } from "@i18n";
import { useParams } from "react-router-dom";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { useCallback, useEffect, useState } from "react";
import { formCategories } from "@Helpers";
import axiosWrapper from "@AxiosWrapper";
import type { GetFormTemplateResponse } from "@ServerTypes/portal/portal.schema";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import Text from "@CubeComponents/html/Text/Text";

interface ProjectDetailsTemplateProps {
  /** The staged project, plus the `formTemplate` answers this component folds into it. */
  toSaveProjectData: ProjectData & { formTemplate?: unknown };
  setToSaveProjectData: React.Dispatch<React.SetStateAction<ProjectData>>;
  editMode?: boolean;
  isProjectIdEditable?: boolean;
  /** `null` is the declared default; `CreateProjectModal` and the settings page both pass a real one. */
  formValidator?: ReturnType<typeof useFormValidator> | null;
}

function ProjectDetailsTemplate({
  toSaveProjectData,
  setToSaveProjectData,
  editMode = true,
  isProjectIdEditable = true,
  formValidator = null,
}: ProjectDetailsTemplateProps) {
  const { t } = useTranslation();
  const params = useParams();
  // `{}` until the fetch resolves; `template` is only read after a `Object.keys(...).length` gate.
  const [formTemplate, setFormTemplate] = useState<Partial<NonNullable<GetFormTemplateResponse["data"]>>>({});

  const getFormTemplate = useCallback(async () => {
    axiosWrapper
      .post<GetFormTemplateResponse>(`${getBackendUrl()}/portal/getFormTemplate`, {
        resourceType: formCategories.projects,
        projectId: params.projectId,
      })
      .then(({ data }) => {
        setFormTemplate(data.data || {});
      });
  }, [params.projectId]);

  useEffect(() => {
    getFormTemplate();
  }, [getFormTemplate]);

  const handleProjectDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToSaveProjectData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleCustomFieldsData = (data: unknown) => {
    setToSaveProjectData((prev) => ({ ...prev, formTemplate: data }));
    // `data` is the edited template, handed straight back from `FormPreview`; the response type
    // declares the narrower fetched shape, so the write is asserted at this one point.
    setFormTemplate((prev) => ({ ...prev, template: data }) as Partial<NonNullable<GetFormTemplateResponse["data"]>>);
  };
  return (
    <div className="flex flex-col gap-10" id="popForm">
      <div className="flex flex-col gap-7 mt-6">
        <Text className="text-lg font-semibold text-[#192048]" data-testid="defaultFields">
          {t("Default Fields")}
        </Text>

        <div className="flex gap-20">
          <div className="flex flex-col gap-10 w-full">
            <div className="flex">
              <Text className="text-base font-semibold" data-testid="projectId">
                {t("Project ID")}*
              </Text>
              <div className="w-3/4 ms-auto" data-testid="projectIdValue">
                {editMode && isProjectIdEditable ? (
                  <TextFieldComponent
                    placeholder={t("Project ID")}
                    name="projectId"
                    onChange={handleProjectDetailsChange}
                    value={toSaveProjectData.projectId}
                  />
                ) : (
                  toSaveProjectData.projectId
                )}
              </div>
            </div>

            <div className="flex">
              <Text className="text-base font-semibold" data-testid="projectName">
                {t("Project Name")}*
              </Text>
              <div className="w-3/4 ms-auto" data-testid="projectNameValue">
                {editMode ? (
                  <TextFieldComponent
                    placeholder={t("Project Name")}
                    name="description"
                    onChange={handleProjectDetailsChange}
                    value={toSaveProjectData.description}
                  />
                ) : (
                  toSaveProjectData.description
                )}
              </div>
            </div>
          </div>
        </div>

        {Object.keys(formTemplate).length > 0 && formTemplate.template!.length > 0 && (
          <Text className="text-lg font-semibold text-[#192048]" data-testid="customFields">
            {t("Custom Fields")}
          </Text>
        )}
        {Object.keys(formTemplate).length > 0 && formTemplate.template!.length > 0 && (
          <div>
            <FormPreview
              // `null` is this component's declared default, but `FormPreview` requires a validator.
              // Both real call sites pass one; erased assertion at the single boundary.
              formValidator={formValidator!}
              formData={formTemplate}
              setFormData={setFormTemplate}
              allowBorder={false}
              allowPadding={false}
              disabled={!editMode}
              fullWidthInput
              handleData={handleCustomFieldsData}
              headingFontSize="text-lg"
              showAnswersOnly={!editMode}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectDetailsTemplate;
